import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_FACTORY_ABI } from '../abis/ChallengeFactoryABI'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import { CHALLENGE_STATE } from '../types/challenge'
import type { ChallengeDetail } from '../types/challenge'

const BATCH_SIZE = 2000
const LOOK_BACK_BLOCKS = 100_000

export interface WinnerInfo {
  address: string
  isClaimed: boolean
  isCurrentUser: boolean
}

export interface SettlementData {
  challenge: ChallengeDetail
  payoutPerWinner: bigint
  totalPool: bigint
  winners: WinnerInfo[]
  finalizeTxHash: string | null
}

export function parseClaimError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('user rejected') || msg.includes('ACTION_REJECTED'))
      return '트랜잭션을 거부했습니다'
    if (msg.includes('not winner') || msg.includes('not a winner')) return '완주자가 아닙니다'
    if (msg.includes('already claimed')) return '이미 정산을 수령했습니다'
    if (msg.includes('not finalized')) return '챌린지가 아직 정산되지 않았습니다'
    return msg.slice(0, 120)
  }
  return '정산 수령에 실패했습니다'
}

async function fetchSettlementData(
  challengeAddress: string,
  userAddress: string | null,
): Promise<SettlementData> {
  const provider = getReadProvider()
  const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, provider)

  const [
    state,
    creator,
    challengeType,
    startTime,
    endTime,
    entryFee,
    winnerCount,
    minParticipants,
    maxParticipants,
    participantCount,
    roundDeadlinesBig,
    payoutPerWinner,
  ] = await Promise.all([
    contract.state() as Promise<bigint>,
    contract.creator() as Promise<string>,
    contract.challengeType() as Promise<bigint>,
    contract.startTime() as Promise<bigint>,
    contract.endTime() as Promise<bigint>,
    contract.entryFee() as Promise<bigint>,
    contract.winnerCount() as Promise<bigint>,
    contract.minParticipants() as Promise<bigint>,
    contract.maxParticipants() as Promise<bigint>,
    contract.participantCount() as Promise<bigint>,
    contract.getRoundDeadlines() as Promise<bigint[]>,
    contract.payoutPerWinner() as Promise<bigint>,
  ])

  // Get challenge name from factory event
  let name = ''
  const factoryAddress = (import.meta.env.VITE_FACTORY_ADDRESS as string | undefined) ?? ''
  if (factoryAddress) {
    const factory = new ethers.Contract(factoryAddress, CHALLENGE_FACTORY_ABI, provider)
    const currentBlock = await provider.getBlockNumber()
    const fromBlock = Math.max(0, currentBlock - LOOK_BACK_BLOCKS)
    for (let from = fromBlock; from <= currentBlock; from += BATCH_SIZE) {
      const to = Math.min(from + BATCH_SIZE - 1, currentBlock)
      const logs = await factory.queryFilter(
        factory.filters.ChallengeCreated(challengeAddress),
        from,
        to,
      )
      if (logs.length > 0) {
        name = (logs[0] as ethers.EventLog).args.name as string
        break
      }
    }
  }

  // Off-chain description
  let description: string | null = null
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  try {
    const resp = await fetch(`${apiBase}/api/challenges/${challengeAddress}`)
    if (resp.ok) {
      const data = (await resp.json()) as { description?: string }
      description = data.description ?? null
    }
  } catch {
    // non-fatal
  }

  const challenge: ChallengeDetail = {
    address: challengeAddress,
    name,
    creator: (creator as string).toLowerCase(),
    challengeType: Number(challengeType),
    state: Number(state),
    startTime: Number(startTime),
    endTime: Number(endTime),
    entryFee: BigInt(entryFee.toString()),
    winnerCount: Number(winnerCount),
    participantCount: Number(participantCount),
    maxParticipants: Number(maxParticipants),
    minParticipants: Number(minParticipants),
    roundDeadlines: (roundDeadlinesBig as bigint[]).map(Number),
    isParticipant: false,
    description,
  }

  const winners: WinnerInfo[] = []
  let finalizeTxHash: string | null = null

  if (Number(state) === CHALLENGE_STATE.ENDED) {
    const winnerAddresses = await (contract.getWinners() as Promise<string[]>)

    const claimedStatuses = await Promise.all(
      winnerAddresses.map((addr) => contract.claimed(addr) as Promise<boolean>),
    )

    for (let i = 0; i < winnerAddresses.length; i++) {
      winners.push({
        address: winnerAddresses[i].toLowerCase(),
        isClaimed: claimedStatuses[i],
        isCurrentUser: userAddress?.toLowerCase() === winnerAddresses[i].toLowerCase(),
      })
    }

    // Get finalize tx hash from ChallengeFinalized event
    const currentBlock = await provider.getBlockNumber()
    const fromBlock = Math.max(0, currentBlock - LOOK_BACK_BLOCKS)
    for (let from = fromBlock; from <= currentBlock; from += BATCH_SIZE) {
      const to = Math.min(from + BATCH_SIZE - 1, currentBlock)
      const logs = await contract.queryFilter(contract.filters.ChallengeFinalized(), from, to)
      if (logs.length > 0) {
        finalizeTxHash = logs[0].transactionHash
        break
      }
    }
  }

  const totalPool = BigInt(entryFee.toString()) * BigInt(Number(participantCount))

  return {
    challenge,
    payoutPerWinner: BigInt(payoutPerWinner.toString()),
    totalPool,
    winners,
    finalizeTxHash,
  }
}

export function useSettlementResult(challengeAddress: string, userAddress: string | null) {
  return useQuery<SettlementData>({
    queryKey: ['settlement', challengeAddress, userAddress],
    queryFn: () => fetchSettlementData(challengeAddress, userAddress),
    enabled: Boolean(challengeAddress),
    staleTime: 30_000,
  })
}

export function useClaim(challengeAddress: string) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')
      const provider = new ethers.BrowserProvider(window.ethereum)
      const signer = await provider.getSigner()
      const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, signer)
      const tx = await (contract.claim as () => Promise<ethers.TransactionResponse>)()
      await tx.wait()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settlement', challengeAddress] })
    },
  })
}
